import React, { useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Badge,
  Accordion,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaGraduationCap,
  FaPlus,
  FaTrash,
  FaSave,
  FaBook,
  FaVideo,
  FaFilePdf,
  FaQuestionCircle,
  FaArrowLeft,
  FaTimes,
  FaYoutube,
  FaUpload,
} from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

const CrearCursoPage = ({ usuario }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [categorias, setCategorias] = useState(["Seguridad", "Inducción", "Calidad", "Salud Ocupacional", "Habilidades Blandas"]);
  const [usuariosEmpresa, setUsuariosEmpresa] = useState([]);
  const [firmasPorDni, setFirmasPorDni] = useState({});
  const [mostrarNuevaCategoria, setMostrarNuevaCategoria] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState("");

  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    categoria: "Seguridad",
    fechaInicio: "",
    fechaFin: "",
    duracionHoras: 1,
    capacitadorDni: "",
    capacitadorSunafil: "",
    cargoCapacitador: "",
    firmaCapacitadorUrl: "",
    organizadorDni: "",
    organizador: "",
    cargoOrganizador: "",
    firmaOrganizadorUrl: "",
    modulos: [],
  });

  // Cargar categorías personalizadas si existen
  React.useEffect(() => {
    const cargarCategorias = async () => {
      try {
        const rucEmpresa = usuario?.ruc || "";
        const categoriasRef = collection(db, `in_capacitacion/${rucEmpresa}/categorias`);
        const snapshot = await getDocs(categoriasRef);
        const docs = snapshot.docs.map(doc => doc.data().nombre);
        if (docs.length > 0) {
          setCategorias(prev => [...new Set([...prev, ...docs])]);
        }
      } catch (err) {
        console.error("Error al cargar categorías:", err);
      }
    };
    if (usuario?.ruc) cargarCategorias();
  }, [usuario]);

  React.useEffect(() => {
    const cargarUsuariosYFirmas = async () => {
      try {
        const rucEmpresa = usuario?.ruc || "";
        const qUsuarios = query(
          collection(db, "users"),
          where("rucEmpresa", "==", rucEmpresa)
        );
        const usersSnap = await getDocs(qUsuarios);
        const usersData = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsuariosEmpresa(usersData);

        const mapaFirmas = {};
        const dnis = usersData
          .map((u) => String(u.dni || u.id || ""))
          .filter(Boolean);
        const CHUNK_SIZE = 30;
        for (let i = 0; i < dnis.length; i += CHUNK_SIZE) {
          const chunk = dnis.slice(i, i + CHUNK_SIZE);
          const qFirmas = query(
            collection(db, "firmas"),
            where("__name__", "in", chunk)
          );
          const firmasSnap = await getDocs(qFirmas);
          firmasSnap.forEach((d) => {
            const data = d.data() || {};
            const dni = data.dni || d.id;
            if (!dni) return;
            if (data.ruc && data.ruc !== rucEmpresa) return;
            mapaFirmas[String(dni)] = data.link || data.firmaUrl || data.url || "";
          });
        }
        setFirmasPorDni(mapaFirmas);
      } catch (err) {
        console.error("Error al cargar usuarios/firmas:", err);
      }
    };
    if (usuario?.ruc) cargarUsuariosYFirmas();
  }, [usuario]);

  const nombreCompletoUsuario = (user) =>
    `${user?.name || ""} ${user?.apellidos || ""}`.trim();

  const actualizarPersonaDesdeDni = (tipo, dni) => {
    const user = usuariosEmpresa.find((u) => String(u.dni || u.id) === String(dni));
    const nombre = user ? nombreCompletoUsuario(user) : "";
    const cargo = user?.cargo || "";
    const firma = firmasPorDni[dni] || "";

    if (tipo === "capacitador") {
      setFormData((prev) => ({
        ...prev,
        capacitadorDni: dni,
        capacitadorSunafil: nombre,
        cargoCapacitador: cargo,
        firmaCapacitadorUrl: firma,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      organizadorDni: dni,
      organizador: nombre,
      cargoOrganizador: cargo,
      firmaOrganizadorUrl: firma,
    }));
  };

  // Agregar nuevo módulo
  const agregarModulo = () => {
    setFormData({
      ...formData,
      modulos: [
        ...formData.modulos,
        {
          titulo: "",
          descripcion: "",
          videoURL: "",
          pdfURL: "",
          preguntas: [],
        },
      ],
    });
  };

  // Eliminar módulo
  const eliminarModulo = (index) => {
    const nuevosModulos = formData.modulos.filter((_, i) => i !== index);
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Actualizar módulo
  const actualizarModulo = (index, campo, valor) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[index] = { ...nuevosModulos[index], [campo]: valor };
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Agregar pregunta a módulo
  const agregarPregunta = (moduloIndex) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas.push({
      pregunta: "",
      opciones: ["", ""],
      correcta: 0,
    });
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Eliminar pregunta
  const eliminarPregunta = (moduloIndex, preguntaIndex) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas = nuevosModulos[
      moduloIndex
    ].preguntas.filter((_, i) => i !== preguntaIndex);
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Actualizar pregunta
  const actualizarPregunta = (moduloIndex, preguntaIndex, campo, valor) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas[preguntaIndex][campo] = valor;
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Agregar opción a pregunta
  const agregarOpcion = (moduloIndex, preguntaIndex) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas[preguntaIndex].opciones.push("");
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Eliminar opción
  const eliminarOpcion = (moduloIndex, preguntaIndex, opcionIndex) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas[preguntaIndex].opciones = nuevosModulos[
      moduloIndex
    ].preguntas[preguntaIndex].opciones.filter((_, i) => i !== opcionIndex);
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Actualizar opción
  const actualizarOpcion = (
    moduloIndex,
    preguntaIndex,
    opcionIndex,
    valor
  ) => {
    const nuevosModulos = [...formData.modulos];
    nuevosModulos[moduloIndex].preguntas[preguntaIndex].opciones[
      opcionIndex
    ] = valor;
    setFormData({ ...formData, modulos: nuevosModulos });
  };

  // Validar formulario
  const validarFormulario = () => {
    if (!formData.nombre.trim()) {
      setError("El nombre del curso es obligatorio");
      return false;
    }

    if (!formData.descripcion.trim()) {
      setError("La descripción del curso es obligatoria");
      return false;
    }

    if (formData.modulos.length === 0) {
      setError("Debes agregar al menos un módulo");
      return false;
    }

    for (let i = 0; i < formData.modulos.length; i++) {
      const modulo = formData.modulos[i];

      if (!modulo.titulo.trim()) {
        setError(`El módulo ${i + 1} necesita un título`);
        return false;
      }

      if (!modulo.descripcion.trim()) {
        setError(`El módulo ${i + 1} necesita una descripción`);
        return false;
      }

      // Validar preguntas si existen
      for (let j = 0; j < modulo.preguntas.length; j++) {
        const pregunta = modulo.preguntas[j];

        if (!pregunta.pregunta.trim()) {
          setError(
            `La pregunta ${j + 1} del módulo ${i + 1} no puede estar vacía`
          );
          return false;
        }

        if (pregunta.opciones.length < 2) {
          setError(
            `La pregunta ${j + 1} del módulo ${i + 1} necesita al menos 2 opciones`
          );
          return false;
        }

        for (let k = 0; k < pregunta.opciones.length; k++) {
          if (!pregunta.opciones[k].trim()) {
            setError(
              `La opción ${k + 1} de la pregunta ${j + 1} del módulo ${
                i + 1
              } no puede estar vacía`
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  // Guardar curso
  const guardarCurso = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validarFormulario()) {
      return;
    }

    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";

      const cursoData = {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        asignadoA: ["TODOS"],
        activo: true,
        tipoRegistroSunafil: "CAPACITACION",
        horasSunafil: Number(formData.duracionHoras) || 1,
        capacitadorDni: (formData.capacitadorDni || "").trim(),
        capacitadorSunafil: (formData.capacitadorSunafil || "").trim(),
        cargoCapacitador: (formData.cargoCapacitador || "").trim(),
        firmaCapacitadorUrl: (formData.firmaCapacitadorUrl || "").trim(),
        organizadorDni: (formData.organizadorDni || "").trim(),
        organizador: (formData.organizador || "").trim(),
        cargoOrganizador: (formData.cargoOrganizador || "").trim(),
        firmaOrganizadorUrl: (formData.firmaOrganizadorUrl || "").trim(),
        sedeAdministrativa:
          "OTR.MZ RE LOTE 05 MZA. RE LOTE. 05 C.H. LA ESTANCIA DE LURIN",
        temaSunafil: formData.nombre.trim(),
        codigoFormatoSunafil: "DIRIS-SST-F-006",
        versionFormatoSunafil: "01",
        fechaFormatoSunafil: "28/02/23",
        categoria: formData.categoria === "OTRA" ? nuevaCategoria.trim() : formData.categoria,
        fechaInicio: formData.fechaInicio || null,
        fechaFin: formData.fechaFin || null,
        duracionHoras: Number(formData.duracionHoras) || 1,
        modulos: formData.modulos.map((modulo, index) => ({
          titulo: modulo.titulo.trim(),
          descripcion: modulo.descripcion.trim(),
          videoURL: modulo.videoURL.trim(),
          pdfURL: modulo.pdfURL.trim(),
          duracionMinutos: Number(modulo.duracionMinutos) || 10,
          obligatorio: modulo.obligatorio !== false,
          orden: index + 1,
          preguntas: modulo.preguntas.map((pregunta) => ({
            pregunta: pregunta.pregunta.trim(),
            opciones: pregunta.opciones.map((op) => op.trim()),
            correcta: pregunta.correcta,
          })),
        })),
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
      };

      // Si es una nueva categoría, guardarla para futuras referencias
      if (formData.categoria === "OTRA" && nuevaCategoria.trim()) {
        const catRef = collection(db, `in_capacitacion/${rucEmpresa}/categorias`);
        await addDoc(catRef, { nombre: nuevaCategoria.trim(), fecha: new Date() });
      }

      const cursosRef = collection(
        db,
        `in_capacitacion/${rucEmpresa}/programas`
      );
      await addDoc(cursosRef, cursoData);

      // AUDITORÍA
      try {
        await logBusinessAction("CREACION_CURSO", "GestionCapacitaciones", {
          curso: cursoData.nombre,
          categoria: cursoData.categoria,
          duracionHoras: cursoData.duracionHoras,
          cantidadModulos: cursoData.modulos.length
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de creación de curso", e);
      }

      setSuccess("¡Curso creado exitosamente!");

      setTimeout(() => {
        navigate("/capacitaciones");
      }, 2000);
    } catch (error) {
      console.error("Error al crear curso:", error);
      setError("Error al crear el curso. Por favor, intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">Guardando curso...</p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado plomo para Capacitaciones */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderBottom: "3px solid #8b9dc3",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaGraduationCap className="me-3" />
                Crear Nuevo Curso
              </h2>
              <p className="mb-0 opacity-75">
                Completa la información del curso y sus módulos
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con botón volver */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                      }}
                    >
                      <FaBook size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Formulario de Creación</h5>
                    <p className="text-muted mb-0 small">
                      Completa todos los campos requeridos para crear el curso
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate("/capacitaciones")}
                      className="d-flex align-items-center gap-2"
                      style={{
                        borderRadius: "10px",
                        borderColor: "#667eea",
                        color: "#667eea",
                        marginLeft: "auto",
                      }}
                    >
                      <FaArrowLeft size="16px" />
                      Volver al Dashboard
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Alertas */}
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError("")}
                className="mb-4"
                style={{ borderRadius: "10px" }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                variant="success"
                dismissible
                onClose={() => setSuccess("")}
                className="mb-4"
                style={{ borderRadius: "10px" }}
              >
                {success}
              </Alert>
            )}

            <Form onSubmit={guardarCurso}>
              {/* Información del Curso */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <h5 className="mb-0 fw-semibold">
                    <FaBook className="me-2" />
                    Información del Curso
                  </h5>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Nombre del Curso *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ej: Introducción a la Seguridad Laboral"
                      value={formData.nombre}
                      onChange={(e) =>
                        setFormData({ ...formData, nombre: e.target.value })
                      }
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Categoría *</Form.Label>
                        <Form.Select
                          value={formData.categoria}
                          onChange={(e) => {
                            setFormData({ ...formData, categoria: e.target.value });
                            setMostrarNuevaCategoria(e.target.value === "OTRA");
                          }}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        >
                          {categorias.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                          <option value="OTRA">+ Agregar Nueva Categoría</option>
                        </Form.Select>
                      </Form.Group>
                      {mostrarNuevaCategoria && (
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            placeholder="Nombre de la nueva categoría"
                            value={nuevaCategoria}
                            onChange={(e) => setNuevaCategoria(e.target.value)}
                            style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                          />
                        </Form.Group>
                      )}
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Fecha Inicio</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.fechaInicio}
                          onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Fecha Fin</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.fechaFin}
                          onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Duración (Horas)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={formData.duracionHoras}
                          onChange={(e) => setFormData({ ...formData, duracionHoras: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Descripción del Curso *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Describe el contenido y objetivos del curso..."
                      value={formData.descripcion}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          descripcion: e.target.value,
                        })
                      }
                      required
                      style={{
                        borderRadius: "10px",
                        border: "2px solid #e9ecef",
                      }}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">DNI Capacitador</Form.Label>
                        <Form.Select
                          value={formData.capacitadorDni || ""}
                          onChange={(e) => actualizarPersonaDesdeDni("capacitador", e.target.value)}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        >
                          <option value="">Seleccione un colaborador</option>
                          {usuariosEmpresa.map((u) => {
                            const dni = String(u.dni || u.id || "");
                            const nombre = nombreCompletoUsuario(u) || dni;
                            return (
                              <option key={`cap-create-${dni}`} value={dni}>
                                {dni} - {nombre}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Capacitador</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.capacitadorSunafil || ""}
                          onChange={(e) => setFormData({ ...formData, capacitadorSunafil: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Cargo Capacitador</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.cargoCapacitador || ""}
                          onChange={(e) => setFormData({ ...formData, cargoCapacitador: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Firma Capacitador (URL)</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.firmaCapacitadorUrl || ""}
                          onChange={(e) => setFormData({ ...formData, firmaCapacitadorUrl: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">DNI Organizador</Form.Label>
                        <Form.Select
                          value={formData.organizadorDni || ""}
                          onChange={(e) => actualizarPersonaDesdeDni("organizador", e.target.value)}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        >
                          <option value="">Seleccione un colaborador</option>
                          {usuariosEmpresa.map((u) => {
                            const dni = String(u.dni || u.id || "");
                            const nombre = nombreCompletoUsuario(u) || dni;
                            return (
                              <option key={`org-create-${dni}`} value={dni}>
                                {dni} - {nombre}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Organizador</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.organizador || ""}
                          onChange={(e) => setFormData({ ...formData, organizador: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Cargo Organizador</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.cargoOrganizador || ""}
                          onChange={(e) => setFormData({ ...formData, cargoOrganizador: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Firma Organizador (URL)</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.firmaOrganizadorUrl || ""}
                          onChange={(e) => setFormData({ ...formData, firmaOrganizadorUrl: e.target.value })}
                          style={{ borderRadius: "10px", border: "2px solid #e9ecef" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Módulos */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaBook className="me-2" />
                    <div className="flex-grow-1">
                      <h5 className="mb-0 fw-semibold">Módulos del Curso</h5>
                      <small className="opacity-75">
                        Agrega los módulos y contenido del curso
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge
                        bg="light"
                        text="dark"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {formData.modulos.length} módulos
                      </Badge>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  {formData.modulos.length === 0 ? (
                    <div className="text-center py-5">
                      <div
                        className="bg-light rounded-circle mx-auto mb-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FaBook size={32} className="text-muted" />
                      </div>
                      <h5 className="text-muted mb-2">No hay módulos agregados</h5>
                      <p className="text-muted mb-3">
                        Comienza agregando el primer módulo del curso
                      </p>
                      <Button
                        variant="outline-primary"
                        onClick={agregarModulo}
                        className="d-flex align-items-center gap-2"
                        style={{
                          borderRadius: "10px",
                          borderColor: "#667eea",
                          color: "#667eea",
                          margin: "0 auto",
                        }}
                      >
                        <FaPlus size="14px" />
                        Agregar Primer Módulo
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Accordion className="mb-3">
                        {formData.modulos.map((modulo, moduloIndex) => (
                          <Accordion.Item
                            key={moduloIndex}
                            eventKey={moduloIndex.toString()}
                            style={{ borderRadius: "10px", marginBottom: "10px", border: "1px solid #e9ecef" }}
                          >
                            <Accordion.Header>
                              <div className="d-flex align-items-center w-100">
                                <Badge
                                  style={{
                                    fontSize: "0.75rem",
                                    background:
                                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    border: "none",
                                    marginRight: "10px",
                                  }}
                                  className="px-2 py-1"
                                >
                                  {moduloIndex + 1}
                                </Badge>
                                <span className="fw-semibold">
                                  {modulo.titulo || `Módulo ${moduloIndex + 1}`}
                                </span>
                                {modulo.preguntas.length > 0 && (
                                  <Badge bg="info" className="ms-2" style={{ fontSize: "0.7rem" }}>
                                    {modulo.preguntas.length} preguntas
                                  </Badge>
                                )}
                              </div>
                            </Accordion.Header>
                            <Accordion.Body style={{ backgroundColor: "#fff" }}>
                              {/* Información del Módulo */}
                              <div className="mb-3">
                                <Form.Label className="fw-semibold">
                                  Título del Módulo *
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="Ej: Introducción a la Seguridad"
                                  value={modulo.titulo}
                                  onChange={(e) =>
                                    actualizarModulo(
                                      moduloIndex,
                                      "titulo",
                                      e.target.value
                                    )
                                  }
                                  required
                                  style={{
                                    borderRadius: "10px",
                                    border: "2px solid #e9ecef",
                                  }}
                                />
                              </div>

                              <div className="mb-3">
                                <Form.Label className="fw-semibold">
                                  Descripción del Módulo *
                                </Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  placeholder="Describe el contenido del módulo..."
                                  value={modulo.descripcion}
                                  onChange={(e) =>
                                    actualizarModulo(
                                      moduloIndex,
                                      "descripcion",
                                      e.target.value
                                    )
                                  }
                                  required
                                  style={{
                                    borderRadius: "10px",
                                    border: "2px solid #e9ecef",
                                  }}
                                />
                              </div>

                              <Row>
                                <Col md={6}>
                                  <div className="mb-3">
                                    <Form.Label className="fw-semibold">
                                      <FaVideo className="me-2" />
                                      URL del Video (YouTube)
                                    </Form.Label>
                                    <InputGroup>
                                      <InputGroup.Text
                                        style={{
                                          borderRadius: "10px 0 0 10px",
                                          background: "#f8f9fa",
                                          border: "2px solid #e9ecef",
                                          borderRight: "none",
                                        }}
                                      >
                                        <FaYoutube style={{ color: "#FF0000" }} />
                                      </InputGroup.Text>
                                      <Form.Control
                                        type="url"
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        value={modulo.videoURL}
                                        onChange={(e) =>
                                          actualizarModulo(
                                            moduloIndex,
                                            "videoURL",
                                            e.target.value
                                          )
                                        }
                                        style={{
                                          borderRadius: "0 10px 10px 0",
                                          border: "2px solid #e9ecef",
                                          borderLeft: "none",
                                        }}
                                      />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                      Opcional: Enlace de YouTube para el video del
                                      módulo
                                    </Form.Text>
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <div className="mb-3">
                                    <Form.Label className="fw-semibold">
                                      <FaFilePdf className="me-2" />
                                      URL del PDF
                                    </Form.Label>
                                    <InputGroup>
                                      <InputGroup.Text
                                        style={{
                                          borderRadius: "10px 0 0 10px",
                                          background: "#f8f9fa",
                                          border: "2px solid #e9ecef",
                                          borderRight: "none",
                                        }}
                                      >
                                        <FaUpload />
                                      </InputGroup.Text>
                                      <Form.Control
                                        type="url"
                                        placeholder="https://..."
                                        value={modulo.pdfURL}
                                        onChange={(e) =>
                                          actualizarModulo(
                                            moduloIndex,
                                            "pdfURL",
                                            e.target.value
                                          )
                                        }
                                        style={{
                                          borderRadius: "0 10px 10px 0",
                                          border: "2px solid #e9ecef",
                                          borderLeft: "none",
                                        }}
                                      />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                      Opcional: Enlace al material PDF
                                    </Form.Text>
                                  </div>
                                </Col>
                              </Row>

                              {/* Preguntas del Módulo */}
                              <div
                                className="mt-4 p-3"
                                style={{
                                  background: "#f8f9fa",
                                  borderRadius: "10px",
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0 fw-semibold">
                                    <FaQuestionCircle className="me-2" />
                                    Preguntas de Evaluación
                                  </h6>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => agregarPregunta(moduloIndex)}
                                    style={{ borderRadius: "8px" }}
                                  >
                                    <FaPlus className="me-1" />
                                    Agregar Pregunta
                                  </Button>
                                </div>

                                {modulo.preguntas.length === 0 ? (
                                  <p className="text-muted text-center mb-0">
                                    No hay preguntas. Agrega preguntas para evaluar
                                    el conocimiento.
                                  </p>
                                ) : (
                                  modulo.preguntas.map((pregunta, preguntaIndex) => (
                                    <Card
                                      key={preguntaIndex}
                                      className="mb-3 border-0 shadow-sm"
                                      style={{ borderRadius: "10px" }}
                                    >
                                      <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                          <Badge bg="secondary">
                                            Pregunta {preguntaIndex + 1}
                                          </Badge>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() =>
                                              eliminarPregunta(
                                                moduloIndex,
                                                preguntaIndex
                                              )
                                            }
                                            style={{ borderRadius: "6px" }}
                                          >
                                            <FaTimes />
                                          </Button>
                                        </div>

                                        <Form.Group className="mb-3">
                                          <Form.Label className="fw-semibold small">
                                            Texto de la Pregunta
                                          </Form.Label>
                                          <Form.Control
                                            type="text"
                                            placeholder="¿Cuál es...?"
                                            value={pregunta.pregunta}
                                            onChange={(e) =>
                                              actualizarPregunta(
                                                moduloIndex,
                                                preguntaIndex,
                                                "pregunta",
                                                e.target.value
                                              )
                                            }
                                            required
                                            style={{
                                              borderRadius: "8px",
                                              border: "1px solid #dee2e6",
                                            }}
                                          />
                                        </Form.Group>

                                        <Form.Label className="fw-semibold small">
                                          Opciones de Respuesta
                                        </Form.Label>
                                        {pregunta.opciones.map(
                                          (opcion, opcionIndex) => (
                                            <InputGroup
                                              key={opcionIndex}
                                              className="mb-2"
                                            >
                                              <InputGroup.Radio
                                                checked={
                                                  pregunta.correcta === opcionIndex
                                                }
                                                onChange={() =>
                                                  actualizarPregunta(
                                                    moduloIndex,
                                                    preguntaIndex,
                                                    "correcta",
                                                    opcionIndex
                                                  )
                                                }
                                                style={{ cursor: "pointer" }}
                                              />
                                              <Form.Control
                                                type="text"
                                                placeholder={`Opción ${
                                                  opcionIndex + 1
                                                }`}
                                                value={opcion}
                                                onChange={(e) =>
                                                  actualizarOpcion(
                                                    moduloIndex,
                                                    preguntaIndex,
                                                    opcionIndex,
                                                    e.target.value
                                                  )
                                                }
                                                required
                                                style={{
                                                  borderRadius: "0",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              />
                                              {pregunta.opciones.length > 2 && (
                                                <Button
                                                  variant="outline-danger"
                                                  onClick={() =>
                                                    eliminarOpcion(
                                                      moduloIndex,
                                                      preguntaIndex,
                                                      opcionIndex
                                                    )
                                                  }
                                                  style={{
                                                    borderRadius: "0 8px 8px 0",
                                                  }}
                                                >
                                                  <FaTimes />
                                                </Button>
                                              )}
                                            </InputGroup>
                                          )
                                        )}

                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() =>
                                            agregarOpcion(moduloIndex, preguntaIndex)
                                          }
                                          className="mt-2"
                                          style={{ borderRadius: "6px" }}
                                        >
                                          <FaPlus className="me-1" />
                                          Agregar Opción
                                        </Button>

                                        <Form.Text className="d-block mt-2 text-muted">
                                          Selecciona el radio button de la respuesta
                                          correcta
                                        </Form.Text>
                                      </Card.Body>
                                    </Card>
                                  ))
                                )}
                              </div>

                              {/* Eliminar Módulo */}
                              <div className="text-end mt-3">
                                <Button
                                  variant="outline-danger"
                                  onClick={() => eliminarModulo(moduloIndex)}
                                  style={{ borderRadius: "10px" }}
                                >
                                  <FaTrash className="me-2" />
                                  Eliminar Módulo
                                </Button>
                              </div>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>

                      <Button
                        variant="outline-primary"
                        onClick={agregarModulo}
                        className="w-100"
                        style={{
                          borderRadius: "10px",
                          borderColor: "#667eea",
                          color: "#667eea",
                        }}
                      >
                        <FaPlus className="me-2" />
                        Agregar Otro Módulo
                      </Button>
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Botones de Acción */}
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <Row>
                    <Col md={6}>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate("/capacitaciones")}
                        className="w-100"
                        style={{ borderRadius: "10px" }}
                        disabled={loading}
                      >
                        <FaTimes className="me-2" />
                        Cancelar
                      </Button>
                    </Col>
                    <Col md={6}>
                      <Button
                        type="submit"
                        className="w-100 text-white"
                        disabled={loading}
                        style={{
                          borderRadius: "10px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          border: "none",
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <FaSave className="me-2" />
                            Guardar Curso
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default CrearCursoPage;
